import { useLayoutEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { navFitsInBar } from "@/lib/headerFit";

/**
 * Est-ce que la navigation tient réellement dans la barre ? Un point de rupture
 * ne peut pas le savoir : il ignore les métriques de police, la langue, la
 * longueur du prénom affiché et le fait que l'utilisateur soit admin. La barre
 * mesure donc, à chaque redimensionnement. Voir lib/headerFit.js.
 *
 * Partagé par les deux en-têtes (RDEC-019) : l'accueil décidait encore par un
 * point de rupture (`xl`), les pages internes par la mesure.
 */
export const useNavFit = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  // Les libellés changent avec la langue, les contrôles de droite avec la
  // session : les deux modifient la largeur requise.
  const key = `${language}:${user?.user_id || ""}:${user?.is_admin ? 1 : 0}:${user?.name || ""}:${user?.pro_status || ""}`;
  const barRef = useRef(null);
  const brandRef = useRef(null);
  const navRef = useRef(null);
  const controlsRef = useRef(null);
  const toggleRef = useRef(null);
  const [navFits, setNavFits] = useState(true);

  useLayoutEffect(() => {
    const bar = barRef.current;
    const nav = navRef.current;
    if (!bar || !nav) return undefined;

    const decide = () => {
      // On mesure la disposition dont on décide — nav dépliée, bouton de menu
      // absent — puis on restaure. Sans cela deux erreurs se cumulent : repliée,
      // la nav mesure zéro et se croit à l'aise indéfiniment ; et le bouton de
      // menu, qui n'existe que dans la disposition repliée, ajoute sa propre
      // largeur à la condition de son retrait.
      const toggle = toggleRef.current;
      const navDisplay = nav.style.display;
      const toggleDisplay = toggle ? toggle.style.display : null;
      nav.style.display = "flex";
      if (toggle) toggle.style.display = "none";

      const cs = window.getComputedStyle(bar);
      const padding = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      const gap = parseFloat(cs.columnGap) || 0;
      const fits = navFitsInBar({
        available: bar.clientWidth - padding,
        brand: brandRef.current?.offsetWidth || 0,
        nav: nav.scrollWidth,
        right: controlsRef.current?.offsetWidth || 0,
        gaps: gap * 2,
      });

      nav.style.display = navDisplay;
      if (toggle) toggle.style.display = toggleDisplay;
      setNavFits(fits);
    };

    decide();

    // Les libellés s'élargissent aussi sans qu'aucune fenêtre ne bouge : une
    // police web arrive après le premier rendu et pousse la barre au-delà de ce
    // qu'elle mesurait. On observe donc les contrôles de droite, toujours mis en
    // page — la nav repliée, elle, ne mesure rien — et on remesure une fois les
    // polices chargées.
    if (document.fonts?.ready) document.fonts.ready.then(decide).catch(() => {});
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(decide);
    observer.observe(bar);
    if (controlsRef.current) observer.observe(controlsRef.current);
    return () => observer.disconnect();
  }, [key]);

  return { barRef, brandRef, navRef, controlsRef, toggleRef, navFits };
};
